<template>
    <div class="p-menu">
        <h5 class="text-center">Assessment Builder</h5>
        <Divider />
        <div class="assessment-builder-container">
            <div class="assessment-blocks">
                <div v-if="builderStore.droppedBlocks.length <= 0" class="text-center font-900 text-xl">Please choose your first block. This will be the first step of your assessment.</div>
                <div v-for="(block, index) in builderStore.droppedBlocks" :key="block">
                    <div class="card my-3" :class="[block == builderStore.activeBlock ? 'border-dashed' : 'border-1']" @click="previewBlock(block)">
                        <div class="buttons">
                            <Tag class="mr-2" severity="info" :value="getStepLabel(block.id)" />
                            <Tag v-if="isEntryStep(block.id)" class="mr-2" icon="pi pi-check" severity="success" value="First Block" />
                            <Button class="p-button p-button-danger" icon="pi pi-times" aria-label="Delete" @click="confirmDelete(block.id, index)" />
                        </div>
                        <brick v-for="item in block.attributes" :field="item" :key="item" />
                    </div>
                </div>
            </div>
            <ScrollTop target="parent" :threshold="50" icon="pi pi-arrow-up" />
        </div>
        <div class="flex justify-content-around">
            <Button class="p-button-warning" v-if="assessmentId" label="Check Structure" @click="checkStructure" />
            <Button label="Save" @click="goBack" />
        </div>
        <assessment-structure v-if="structureDialog" @hide-dialog="structureDialog = false" />
    </div>
</template>

<script setup>
import Brick from '@/components/block/brick/Brick.vue';
import AssessmentStructure from './AssessmentStructure.vue';
import { ref, inject } from 'vue';
import { useRouter } from 'vue-router';
import { useToast } from 'primevue/usetoast';
import { useConfirm } from 'primevue/useconfirm';

const router = useRouter();
const toast = useToast();
const confirm = useConfirm();

const builderStore = inject('builderStore');
const stepStore = inject('stepStore');
const assessmentId = inject('assessmentId');
const assessmentStore = inject('assessmentStore');

const structureDialog = ref(false);

const previewBlock = (block) => {
    builderStore.setActiveBlock(block);
    // check for the block id and make the step active as well
    stepStore.setActiveStep(block.id);
};

const isEntryStep = (blockId) => {
    const entryStepBlockId = stepStore.steps?.find((step) => step.entry === 1)?.block_id;
    return blockId === entryStepBlockId;
};

const getStepLabel = (blockId) => {
    return stepStore.steps?.find((step) => step.block_id === blockId)?.label;
};

const checkStructure = async () => {
    // the structure will be set in the assessmentStructure state
    // use the getter of the assessment store to extract the structure later
    if (assessmentId) {
        structureDialog.value = true;
        await assessmentStore.getAssessmentStructure(assessmentId);
    }
};

const confirmDelete = (blockId, blockIndex) => {
    // fetch the step details first using block id
    const stepDetails = stepStore.getStepDetailsByBlockId(blockId);
    confirm.require({
        header: 'Delete Confirmation',
        message: 'Are you sure you want to delete step ' + stepDetails?.id + '?',
        icon: 'pi pi-info-circle',
        accept: async () => {
            const stepDeleted = await stepStore.deleteStep(stepDetails.id);
            if (stepDeleted) {
                // if step is deleted then remove the block as well
                await builderStore.removeBlock(blockIndex);
            }
        },
        reject: () => {
            toast.add({ severity: 'error', summary: 'Rejected', detail: 'Cancelled', life: 5000 });
        },
    });
};

const goBack = () => {
    toast.add({ severity: 'success', summary: 'Success', detail: 'Assessment Submitted', life: 5000 });
    setTimeout(() => {
        return router.push({ name: 'ManageAssessments' });
    }, 2000);
};
</script>

<style lang="scss" scoped>
.assessment-builder-container {
    /* height: 60vh; */
    height: 80%;
    overflow-y: auto;
}

.assessment-blocks {
    padding: 12px;
    cursor: pointer;
}

.active {
    border: 1px solid black;
}

.card {
    position: relative;
}

.buttons {
    position: absolute;
    top: 5px;
    right: 5px;
    display: flex;
    flex-direction: row;
}

.buttons > Button {
    color: #fff;
    padding: 3px 3px;
    margin: auto 2px;
    border-radius: 1px;
    font-size: 0.4em;
    cursor: pointer;
    position: relative;
    overflow: hidden;
    transition: all 0.3s;
}
</style>
